/**
 * The baked county: a planar road graph with names attached.
 *
 * `mapgen` writes this file, the client and the server both read it, and
 * neither of them ever sees an OpenStreetMap tag. Everything downstream of
 * here works on node indices and edge indices.
 *
 * The on-disk shape is columnar rather than an array of objects. A county has
 * thousands of edges and every consumer wants them in typed arrays anyway, so
 * storing parallel number arrays halves the JSON and skips a per-edge object
 * allocation on load.
 */

import { ROAD_CLASS_SPEED } from './constants.ts';

export const COUNTY_FORMAT = 'hotlist.county';
/**
 * Bumped when a baked county can no longer be read correctly.
 *
 * 4 adds the link classes -- on and off ramps -- which shifts every class index
 * after `motorway`. A version 3 file read as version 4 would silently
 * reinterpret every road in the county as a different kind of road, so the
 * loader must refuse it rather than guess.
 */
export const COUNTY_FORMAT_VERSION = 6;

/**
 * Road classes, in the order mapgen writes them. The index into this list is
 * what the edge column stores. OpenStreetMap highway values are collapsed onto
 * these by mapgen; nothing else in the game knows the OSM vocabulary.
 */
export const ROAD_CLASSES = [
  'motorway',
  /**
   * On and off ramps, which are their own thing.
   *
   * They used to be folded into the class of the road they serve, so an on-ramp
   * was 70 mph motorway and joining the freeway was **free**. The router
   * noticed: with 212 places to get on at no cost, every cross-county trip
   * became a freeway trip, including one from Muskego to Hartland that drove
   * 2.8 km *east* up Moorland Road to reach an on-ramp before heading west.
   *
   * A ramp is slow, and that slowness is the whole cost of using a motorway for
   * a short hop. Separating them is what stops the fast road eating the county.
   */
  'motorway_link',
  'trunk',
  'trunk_link',
  'primary',
  'primary_link',
  'secondary',
  'secondary_link',
  'tertiary',
  'tertiary_link',
  'unclassified',
  'residential',
  'service',
  'track',
] as const;

export type RoadClass = (typeof ROAD_CLASSES)[number];

/** OpenStreetMap's own place values, carried through unchanged. */
export type PlaceKind = 'city' | 'town' | 'village' | 'hamlet';

export interface Place {
  name: string;
  /** County-local meters, +x east, +y north. */
  x: number;
  y: number;
  kind: PlaceKind;
  population: number;
}

export interface CountyBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * How this county's meters relate to the round earth.
 *
 * Recorded because a baked county is a flat local frame and anything else that
 * has to land on it arrives in latitude and longitude: camera positions, county
 * boundaries, anything else drawn from a record. Without the origin there is no
 * way to place any of it, and the first thing M2 has to do is place cameras.
 */
export type CountyProjection =
  | { kind: 'utm'; zone: number; originEasting: number; originNorthing: number }
  | { kind: 'synthetic' };

/** Where this county came from, kept so a bake is reproducible. */
export type CountySource =
  | { kind: 'procedural'; seed: number }
  | { kind: 'osm'; extract: string; bbox: [number, number, number, number] };

/** The literal JSON shape on disk. */
export interface CountyFile {
  format: typeof COUNTY_FORMAT;
  version: number;
  /** Fictional. DESIGN.md section 6. */
  name: string;
  /** Fictional state the plates and agencies belong to. */
  state: { abbr: string; name: string };
  source: CountySource;
  projection: CountyProjection;
  generatedAt: string;
  bounds: CountyBounds;
  nodeCount: number;
  edgeCount: number;
  nodes: { x: number[]; y: number[] };
  edges: {
    a: number[];
    b: number[];
    len: number[];
    /** Index into ROAD_CLASSES. */
    cls: number[];
    /** Index into roadNames, or -1 for an unnamed segment. */
    name: number[];
    /** 1 when the edge may only be driven a to b. */
    oneway: number[];
    /**
     * Posted limit in meters per second, or -1 to use the road class default.
     *
     * OpenStreetMap tags maxspeed on nearly every motorway, trunk and primary
     * way and on almost no residential street, which is the right way round:
     * the fast through routes are where the real limit varies and where
     * enforcement does its arithmetic, and a residential street is 25 wherever
     * you find it. So this column is mostly -1 and that is not a gap.
     */
    speed: number[];
    /**
     * Lanes **in one direction**, never the total.
     *
     * The OSM tag it comes from means different things on a oneway and a
     * two-way road, and mapgen resolves that before it gets here; see
     * osm.lane_count. Two is a divided highway carriageway or a four-lane
     * arterial, one is everything else, and one is what an untagged road gets
     * from its class.
     */
    lanes: number[];
  };
  roadNames: string[];
  places: Place[];
  /**
   * Real businesses, from OpenStreetMap.
   *
   * Nothing in this county is invented: the roads, the towns, the camera
   * positions and the departments that run them are all real, so the places
   * work runs between are real too. Same extract, tagged the way the mappers
   * tagged them.
   *
   * Optional on the file, because a county baked before venues existed simply
   * has none. The loader defaults it, so the runtime county always has the
   * array and nothing downstream has to check.
   */
  venues?: CountyVenue[];
  municipalities?: CountyMunicipality[];
}

/**
 * A city, village or town outline.
 *
 * The only polygon in this county file, and it earns its place: a `place` is a
 * *point* with a population, and a point cannot answer whether somebody is in
 * a town or near it. Estimating an extent from population was tried and is off
 * by about a third on the low-density suburbs -- New Berlin's 40,000 people
 * are spread over 36 square miles -- so somebody two miles inside it was
 * told they were near it.
 *
 * Rings are flat `[x, y, x, y, ...]` in county meters, thinned to twenty
 * meters at bake time. Nobody is standing within twenty meters of a town line
 * and wondering which side they are on.
 */
export interface CountyMunicipality {
  name: string;
  /** city, village or town, as OSM's border_type has it. Blank when untagged. */
  kind: string;
  rings: number[][];
}

export interface CountyVenue {
  name: string;
  x: number;
  y: number;
  /** One of the game's venue kinds; see venues.ts. */
  kind: string;
  /** The chain, where it is one. Absent for an independent. */
  brand?: string;
}

/** The in-memory form. Typed arrays, plus the CSR adjacency built on load. */
export interface County {
  name: string;
  state: { abbr: string; name: string };
  source: CountySource;
  projection: CountyProjection;
  generatedAt: string;
  bounds: CountyBounds;

  nodeCount: number;
  edgeCount: number;

  nodeX: Float64Array;
  nodeY: Float64Array;

  edgeA: Int32Array;
  edgeB: Int32Array;
  edgeLen: Float64Array;
  edgeClass: Uint8Array;
  edgeName: Int32Array;
  edgeOneway: Uint8Array;
  /**
   * Lanes in one direction. 1 on most roads, 2 or more on arterials.
   *
   * Load-bearing for two things that look the same and are not: on a
   * multi-lane road cars drive abreast and a faster one moves over, and on a
   * single-lane road it has to cross into oncoming traffic to get past. The
   * second is an offence with a cop in sight and the first is not.
   */
  edgeLanes: Uint8Array;
  /** Free-flow speed in meters per second, posted where known. */
  edgeSpeed: Float64Array;
  /** Free-flow travel time in seconds, precomputed from length and speed. */
  edgeTime: Float64Array;

  roadNames: string[];
  places: Place[];
  /** Real businesses, from OpenStreetMap. See CountyVenue. */
  venues: CountyVenue[];
  municipalities: CountyMunicipality[];

  /**
   * Compressed sparse row adjacency. The edges touching node n are
   * adjEdge[adjStart[n]] up to but not including adjEdge[adjStart[n + 1]].
   */
  adjStart: Int32Array;
  adjEdge: Int32Array;
}

export class CountyFormatError extends Error {
  constructor(message: string) {
    super(`Bad county file: ${message}`);
    this.name = 'CountyFormatError';
  }
}

function requireNumberArray(value: unknown, expected: number, what: string): number[] {
  if (!Array.isArray(value)) throw new CountyFormatError(`${what} is not an array`);
  if (value.length !== expected) {
    throw new CountyFormatError(`${what} has ${value.length} entries, expected ${expected}`);
  }
  return value as number[];
}

/**
 * Validate a parsed county file and build the in-memory form.
 *
 * Strict on purpose. A county with a dangling node index would otherwise
 * surface as a silent NaN somewhere in the renderer an hour later.
 */
export function loadCounty(raw: unknown): County {
  const file = raw as CountyFile;

  if (file?.format !== COUNTY_FORMAT) {
    throw new CountyFormatError(`format is ${String(file?.format)}, expected ${COUNTY_FORMAT}`);
  }
  if (file.version !== COUNTY_FORMAT_VERSION) {
    // A baked county is a generated artifact, so the fix is always the same
    // and there is no reason to make somebody work it out.
    throw new CountyFormatError(
      `version is ${String(file.version)}, this build reads ${COUNTY_FORMAT_VERSION}. ` +
        'Re-bake it: pnpm bake',
    );
  }

  const nodeCount = file.nodeCount | 0;
  const edgeCount = file.edgeCount | 0;
  if (nodeCount <= 0) throw new CountyFormatError('nodeCount is not positive');
  if (edgeCount <= 0) throw new CountyFormatError('edgeCount is not positive');

  const nodeX = Float64Array.from(requireNumberArray(file.nodes?.x, nodeCount, 'nodes.x'));
  const nodeY = Float64Array.from(requireNumberArray(file.nodes?.y, nodeCount, 'nodes.y'));

  const edgeA = Int32Array.from(requireNumberArray(file.edges?.a, edgeCount, 'edges.a'));
  const edgeB = Int32Array.from(requireNumberArray(file.edges?.b, edgeCount, 'edges.b'));
  const edgeLen = Float64Array.from(requireNumberArray(file.edges?.len, edgeCount, 'edges.len'));
  const edgeClass = Uint8Array.from(requireNumberArray(file.edges?.cls, edgeCount, 'edges.cls'));
  const edgeName = Int32Array.from(requireNumberArray(file.edges?.name, edgeCount, 'edges.name'));
  const edgeOneway = Uint8Array.from(
    requireNumberArray(file.edges?.oneway, edgeCount, 'edges.oneway'),
  );
  const postedSpeed = requireNumberArray(file.edges?.speed, edgeCount, 'edges.speed');
  const edgeLanes = Uint8Array.from(requireNumberArray(file.edges?.lanes, edgeCount, 'edges.lanes'));

  const roadNames = Array.isArray(file.roadNames) ? file.roadNames : [];

  // Degree pass, which doubles as the referential integrity check.
  const degree = new Int32Array(nodeCount);
  for (let e = 0; e < edgeCount; e++) {
    const a = edgeA[e] as number;
    const b = edgeB[e] as number;
    if (a < 0 || a >= nodeCount) throw new CountyFormatError(`edge ${e} node a=${a} out of range`);
    if (b < 0 || b >= nodeCount) throw new CountyFormatError(`edge ${e} node b=${b} out of range`);
    if (a === b) throw new CountyFormatError(`edge ${e} is a self loop at node ${a}`);
    if (!((edgeLen[e] as number) > 0)) {
      throw new CountyFormatError(`edge ${e} has length ${String(edgeLen[e])}`);
    }
    if ((edgeClass[e] as number) >= ROAD_CLASSES.length) {
      throw new CountyFormatError(`edge ${e} class index ${String(edgeClass[e])} is unknown`);
    }
    if ((edgeName[e] as number) >= roadNames.length) {
      throw new CountyFormatError(`edge ${e} name index ${String(edgeName[e])} is unknown`);
    }
    degree[a] = (degree[a] as number) + 1;
    degree[b] = (degree[b] as number) + 1;
  }

  // Build CSR.
  const adjStart = new Int32Array(nodeCount + 1);
  for (let n = 0; n < nodeCount; n++) {
    adjStart[n + 1] = (adjStart[n] as number) + (degree[n] as number);
  }
  const adjEdge = new Int32Array(edgeCount * 2);
  const cursor = Int32Array.from(adjStart.subarray(0, nodeCount));
  for (let e = 0; e < edgeCount; e++) {
    const a = edgeA[e] as number;
    const b = edgeB[e] as number;
    adjEdge[cursor[a] as number] = e;
    cursor[a] = (cursor[a] as number) + 1;
    adjEdge[cursor[b] as number] = e;
    cursor[b] = (cursor[b] as number) + 1;
  }

  // Free-flow travel time, which is what routing actually minimizes. A driver
  // takes the fast road, not the short one.
  const edgeSpeed = new Float64Array(edgeCount);
  const edgeTime = new Float64Array(edgeCount);
  for (let e = 0; e < edgeCount; e++) {
    const posted = postedSpeed[e] as number;
    const speed = posted > 0 ? posted : classSpeed(edgeClass[e] as number);
    edgeSpeed[e] = speed;
    edgeTime[e] = (edgeLen[e] as number) / speed;
  }

  return {
    name: file.name,
    state: file.state,
    source: file.source,
    projection: file.projection ?? { kind: 'synthetic' },
    generatedAt: file.generatedAt,
    bounds: file.bounds,
    nodeCount,
    edgeCount,
    nodeX,
    nodeY,
    edgeA,
    edgeB,
    edgeLen,
    edgeClass,
    edgeName,
    edgeOneway,
    edgeLanes,
    edgeSpeed,
    edgeTime,
    roadNames,
    places: Array.isArray(file.places) ? file.places : [],
    // Absent from a county baked before venues existed, which is fine: the job
    // board simply has nowhere to send anybody until it is re-baked.
    venues: Array.isArray(file.venues) ? file.venues : [],
    // Absent in a county baked before format 5, and absent from a procedural
    // one, which has no municipalities to have. Callers fall back to nearest.
    municipalities: Array.isArray(file.municipalities) ? file.municipalities : [],
    adjStart,
    adjEdge,
  };
}

export function classSpeed(classIndex: number): number {
  const name = ROAD_CLASSES[classIndex];
  return (name !== undefined ? ROAD_CLASS_SPEED[name] : undefined) ?? 11.2;
}

export function className(classIndex: number): RoadClass {
  return ROAD_CLASSES[classIndex] ?? 'residential';
}

/** The road name on an edge, or the empty string for unnamed rural segments. */
export function edgeNameOf(county: County, edge: number): string {
  const index = county.edgeName[edge] as number;
  return index < 0 ? '' : (county.roadNames[index] ?? '');
}

/** The node at the far end of `edge` from `node`. */
export function otherNode(county: County, edge: number, node: number): number {
  const a = county.edgeA[edge] as number;
  return a === node ? (county.edgeB[edge] as number) : a;
}

/** Whether `edge` may be entered from `node`. Respects one-way. */
export function edgeEnterableFrom(county: County, edge: number, node: number): boolean {
  if ((county.edgeOneway[edge] as number) === 0) return true;
  return (county.edgeA[edge] as number) === node;
}

/** Straight-line meters between two nodes. */
export function nodeDistance(county: County, a: number, b: number): number {
  const dx = (county.nodeX[a] as number) - (county.nodeX[b] as number);
  const dy = (county.nodeY[a] as number) - (county.nodeY[b] as number);
  return Math.hypot(dx, dy);
}
